import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import SEO from '@/components/common/SEO';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useAppStore } from '@/store';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { CorpusStorage, type StagedCorpusFile } from '@/services/CorpusStorage';
import { getPreferredSovereignDemoModel, getSovereignDemoModels } from '@/lib/demoModels';
import { getSelfServeAddonById } from '@/lib/marketing';
import { MapleLeaf } from '@/components/common/MapleLeaf';
import TrainingPanel from '@/components/training/TrainingPanel';
import TrainedModelsSection from '@/components/training/TrainedModelsSection';
import ComputeCapacityCard from '@/components/training/ComputeCapacityCard';
import ConnectHuggingFaceCard from '@/components/training/ConnectHuggingFaceCard';
import { TrainingService } from '@/services/TrainingService';

import { TERMINAL_STATUSES } from '@/types/training';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

import {
  ArrowRight,
  Bot,
  BrainCircuit,
  Database,
  FileUp,
  MessageSquareText,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';

const ALLOWED_EXTENSIONS = new Set(['txt', 'md', 'csv', 'json', 'jsonl']);
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

type CorpusFile = StagedCorpusFile;

type PurchaseSummary = {
  loading: boolean;
  trainingCredits: number;
  activeTrainingJobs: number;
  activeRecurringAddons: string[];
};

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const Index = () => {

  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { profile } = useProfile();
  const { user } = useAppStore();
  const { models, addChat } = useSupabaseStore();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [corpusFiles, setCorpusFiles] = useState<CorpusFile[]>([]);
  const [corpusLoading, setCorpusLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [purchaseSummary, setPurchaseSummary] = useState<PurchaseSummary>({
    loading: true,
    trainingCredits: 0,
    activeTrainingJobs: 0,
    activeRecurringAddons: [],
  });

  const demoModels = useMemo(() => getSovereignDemoModels(models), [models]);
  const preferredDemoModel = useMemo(() => getPreferredSovereignDemoModel(models), [models]);
  const maplePtModel = useMemo(
    () => demoModels.find((model) => model.provider === 'maplept') || demoModels[0],
    [demoModels]
  );
  const mapleFreeModel = useMemo(
    () => demoModels.find((model) => model.id === 'maplept-free') || demoModels.find((model) => model.name.toLowerCase().includes('free')),
    [demoModels]
  );

  const loadCorpusFiles = useCallback(async () => {
    if (!authUser?.id) {
      setCorpusFiles([]);
      setCorpusLoading(false);
      return;
    }
    setCorpusLoading(true);
    try {
      const files = await CorpusStorage.list(authUser.id);
      files.sort(
        (a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime(),
      );
      setCorpusFiles(files);
    } catch (error) {
      console.error('Failed to load corpus files:', error);
      toast({
        title: 'Corpus unavailable',
        description: 'Could not load your training corpus from CanXP storage.',
        variant: 'destructive',
      });
    } finally {
      setCorpusLoading(false);
    }
  }, [authUser?.id, toast]);

  useEffect(() => {
    loadCorpusFiles();
  }, [loadCorpusFiles]);

  useEffect(() => {
    let cancelled = false;

    const loadPurchaseSummary = async () => {
      if (!authUser?.id) {
        setPurchaseSummary({ loading: false, trainingCredits: 0, activeTrainingJobs: 0, activeRecurringAddons: [] });
        return;
      }

      setPurchaseSummary((prev) => ({ ...prev, loading: true }));

      try {
        const [creditsResult, subscriptionsResult, jobsResult] = await Promise.all([
          supabase
            .from('training_package_credits')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', authUser.id),
          supabase
            .from('user_subscriptions')
            .select('plan_id')
            .eq('user_id', authUser.id)
            .eq('status', 'active'),
          TrainingService.listJobs({ limit: 100 }),
        ]);

        if (creditsResult.error) throw creditsResult.error;
        if (subscriptionsResult.error) throw subscriptionsResult.error;

        const activeRecurringAddons = ((subscriptionsResult.data || []) as Array<{ plan_id: string | null }>)
          .map((subscription) => {
            const planId = subscription.plan_id;
            if (!planId) return null;
            const addon = getSelfServeAddonById(planId);
            if (!addon || addon.billingType !== 'recurring') return null;
            return addon.name;
          })
          .filter((value): value is string => !!value);

        const activeTrainingJobs = (jobsResult.jobs || []).filter(
          (job) => !TERMINAL_STATUSES.includes(job.status)
        ).length;

        if (!cancelled) {
          setPurchaseSummary({
            loading: false,
            trainingCredits: creditsResult.count || 0,
            activeTrainingJobs,
            activeRecurringAddons,
          });
        }
      } catch (error) {
        console.error('Failed to load purchase summary:', error);
        if (!cancelled) {
          setPurchaseSummary({ loading: false, trainingCredits: 0, activeTrainingJobs: 0, activeRecurringAddons: [] });
        }
      }
    };

    void loadPurchaseSummary();

    return () => {
      cancelled = true;
    };
  }, [authUser?.id]);

  const totalCorpusSize = useMemo(
    () => corpusFiles.reduce((sum, file) => sum + file.size, 0),
    [corpusFiles]
  );

  const trainingReady = corpusFiles.length > 0;
  const trainingStatusTitle = !trainingReady
    ? 'Upload corpus'
    : purchaseSummary.trainingCredits > 0
      ? 'Ready to launch'
      : 'Corpus ready';
  const trainingStatusDescription = !trainingReady
    ? 'Add at least one file to continue.'
    : purchaseSummary.trainingCredits > 0
      ? 'You can start a training run now.'
      : 'Buy credits to launch training.';

  const handleOpenFilePicker = useCallback(() => {

    fileInputRef.current?.click();
  }, []);

  const handleUploadFiles = useCallback(async (incomingFiles: File[]) => {
    if (!incomingFiles.length) return;
    if (!authUser?.id) {
      toast({ title: 'Not signed in', variant: 'destructive' });
      return;
    }

    setIsUploading(true);

    try {
      let uploadedCount = 0;

      for (const file of incomingFiles) {
        const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
        if (!ALLOWED_EXTENSIONS.has(ext)) {
          toast({
            title: 'Unsupported file type',
            description: `${file.name} was skipped. Upload TXT, MD, CSV, JSON, or JSONL files.`,
            variant: 'destructive',
          });
          continue;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast({
            title: 'File too large',
            description: `${file.name} exceeds the 2 MB corpus upload limit.`,
            variant: 'destructive',
          });
          continue;
        }

        try {
          await CorpusStorage.upload(authUser.id, file);
          uploadedCount += 1;
        } catch (e: any) {
          toast({
            title: `Upload failed: ${file.name}`,
            description: String(e?.message || e),
            variant: 'destructive',
          });
        }
      }

      if (uploadedCount > 0) {
        toast({
          title: 'Corpus updated',
          description: `${uploadedCount} file${uploadedCount === 1 ? '' : 's'} synced to your CanXP corpus (visible on every device).`,
        });
      }

      await loadCorpusFiles();
    } catch (error) {
      console.error('Failed to upload corpus files:', error);
      toast({
        title: 'Upload failed',
        description: 'We could not save your training corpus files.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [authUser?.id, loadCorpusFiles, toast]);

  const handleInputChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const incomingFiles = Array.from(event.target.files || []);
    await handleUploadFiles(incomingFiles);
  }, [handleUploadFiles]);

  const handleDeleteCorpusFile = useCallback(async (path: string) => {
    try {
      await CorpusStorage.delete(path);
      setCorpusFiles((previous) => previous.filter((file) => file.path !== path));
      toast({ title: 'Corpus file removed' });
    } catch (error) {
      console.error('Failed to delete corpus file:', error);
      toast({
        title: 'Delete failed',
        description: 'We could not remove that corpus file.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const handleClearCorpus = useCallback(async () => {
    if (!authUser?.id) return;
    try {
      const removed = await CorpusStorage.clear(authUser.id);
      setCorpusFiles([]);
      toast({
        title: 'Training corpus cleared',
        description: `${removed} file${removed === 1 ? '' : 's'} removed from CanXP storage.`,
      });
    } catch (error) {
      console.error('Failed to clear corpus:', error);
      toast({
        title: 'Clear failed',
        description: 'We could not clear your training corpus.',
        variant: 'destructive',
      });
    }
  }, [authUser?.id, toast]);

  const handleStartDemoChat = useCallback(async (modelId?: string) => {
    if (!authUser || !modelId || isCreatingChat) return;

    setIsCreatingChat(true);
    try {
      const model = models.find((entry) => entry.id === modelId);
      const newChat = await addChat(authUser.id, `${model?.name || 'Sovereign'} Demo`, modelId);
      if (!newChat?.id) {
        throw new Error('No chat id returned');
      }
      navigate(`/chat/${newChat.id}`);
    } catch (error) {
      console.error('Failed to create demo chat:', error);
      toast({
        title: 'Chat unavailable',
        description: 'We could not launch the selected demo model chat.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingChat(false);
    }
  }, [addChat, authUser, isCreatingChat, models, navigate, toast]);

  return (
    <MainLayout>
      <SEO
        title="Sovereign AI Dashboard"
        description="Prepare your sovereign AI corpus, purchase training services, and test CanXP demo models from a unified dashboard."
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.md,.csv,.json,text/plain,text/markdown,text/csv,application/json"
        multiple
        className="hidden"
        onChange={handleInputChange}
      />
      <div className="container max-w-7xl mx-auto px-4 py-6 space-y-8">
        <section className="relative overflow-hidden rounded-3xl border bg-gradient-to-br from-background via-background to-canada-red/10 p-8 md:p-12">
          <div className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_center,rgba(234,56,76,0.16),transparent_65%)]" />
          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl space-y-5">
              <div className="flex items-center gap-3">
                <MapleLeaf size={42} className="text-canada-red" />
                <Badge variant="outline" className="border-canada-red/40 text-canada-red">
                  Sovereign AI Dashboard
                </Badge>
              </div>
              <div className="space-y-3">
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                  Welcome back{profile?.name ? `, ${profile.name}` : ''}.
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl">
                  Build a private corpus, purchase sovereign model training, and test CanXP demo models before your own model is ready.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleOpenFilePicker} disabled={isUploading}>
                  <Upload className="h-4 w-4 mr-2" />
                  {isUploading ? 'Uploading corpus…' : 'Upload training corpus'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleStartDemoChat(preferredDemoModel?.id)}
                  disabled={!preferredDemoModel || isCreatingChat}
                >
                  <MessageSquareText className="h-4 w-4 mr-2" />
                  Open demo chat
                </Button>
                <Button variant="outline" onClick={() => navigate('/purchase')}>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Purchase training services
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 min-w-full sm:grid-cols-2 lg:min-w-[360px] lg:max-w-[500px]">
              <Card>
                <CardHeader className="p-5 pb-2">
                  <CardDescription>Corpus</CardDescription>
                  <CardTitle className="text-3xl">{corpusFiles.length}</CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5 pt-0 text-sm text-muted-foreground">
                  {formatBytes(totalCorpusSize)} ready.
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="p-5 pb-2">
                  <CardDescription>Credits</CardDescription>
                  <CardTitle className="text-3xl">
                    {purchaseSummary.loading ? '—' : purchaseSummary.trainingCredits}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5 pt-0 text-sm text-muted-foreground">
                  {purchaseSummary.loading
                    ? 'Checking credits…'
                    : purchaseSummary.trainingCredits > 0
                      ? `${purchaseSummary.trainingCredits} ready to launch.`
                      : 'No credits yet.'}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-5 pb-2">
                  <CardDescription>Status</CardDescription>
                  <CardTitle className="text-2xl">{trainingStatusTitle}</CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5 pt-0 text-sm text-muted-foreground">
                  {trainingStatusDescription}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="p-5 pb-2">
                  <CardDescription>Activity</CardDescription>
                  <CardTitle className="text-2xl">
                    {purchaseSummary.loading
                      ? 'Loading…'
                      : `${purchaseSummary.activeTrainingJobs} job${purchaseSummary.activeTrainingJobs === 1 ? '' : 's'} · ${purchaseSummary.activeRecurringAddons.length} add-on${purchaseSummary.activeRecurringAddons.length === 1 ? '' : 's'}`}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 px-5 pb-5 pt-0 text-sm text-muted-foreground">
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border/70 bg-background/40 px-3 py-2">
                    <span>Credits</span>
                    <span className="font-semibold text-foreground">
                      {purchaseSummary.loading ? '—' : purchaseSummary.trainingCredits}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border/70 bg-background/40 px-3 py-2">
                    <span>Jobs</span>
                    <span className="font-semibold text-foreground">
                      {purchaseSummary.loading ? '—' : purchaseSummary.activeTrainingJobs}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border/70 bg-background/40 px-3 py-2">
                    <span>Add-ons</span>
                    <span className="font-semibold text-foreground">
                      {purchaseSummary.loading ? '—' : purchaseSummary.activeRecurringAddons.length}
                    </span>
                  </div>
                  {!purchaseSummary.loading && purchaseSummary.activeRecurringAddons.length > 0 ? (
                    <p className="text-xs">
                      {purchaseSummary.activeRecurringAddons.slice(0, 2).join(', ')}
                      {purchaseSummary.activeRecurringAddons.length > 2
                        ? ` +${purchaseSummary.activeRecurringAddons.length - 2} more`
                        : ''}
                      .
                    </p>
                  ) : null}
                </CardContent>
              </Card>

            </div>

          </div>
        </section>

        <div className="space-y-6">
          <ConnectHuggingFaceCard userId={authUser?.id ?? null} />

          <ComputeCapacityCard />

          <div className="grid gap-6 xl:grid-cols-[minmax(320px,430px)_minmax(0,1fr)] xl:items-start">

            <div className="min-w-0 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-canada-red" />
                    Training Corpus
                  </CardTitle>
                  <CardDescription>
                    Upload TXT, MD, CSV, or JSON files into your private local corpus workspace before purchasing training services.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <button
                    type="button"
                    onClick={handleOpenFilePicker}
                    onDragEnter={() => setDragActive(true)}
                    onDragLeave={() => setDragActive(false)}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragActive(true);
                    }}
                    onDrop={async (event) => {
                      event.preventDefault();
                      setDragActive(false);
                      await handleUploadFiles(Array.from(event.dataTransfer.files || []));
                    }}
                    className={`w-full rounded-2xl border border-dashed p-8 text-left transition ${dragActive ? 'border-canada-red bg-canada-red/5' : 'border-border hover:border-canada-red/50'}`}
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="space-y-2">
                        <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-canada-red/10 text-canada-red">
                          <FileUp className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium">Drop corpus files here or click to upload</p>
                          <p className="text-sm text-muted-foreground">Each file is stored privately in your browser-based sovereign workspace.</p>
                        </div>
                      </div>
                      <Badge variant="outline">TXT · MD · CSV · JSON</Badge>
                    </div>
                  </button>

                  <div className="flex items-center justify-between gap-4">
                    <div className="text-sm text-muted-foreground">
                      {corpusLoading ? 'Loading corpus…' : `${corpusFiles.length} file${corpusFiles.length === 1 ? '' : 's'} ready for training review.`}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={loadCorpusFiles} disabled={corpusLoading}>
                        Refresh
                      </Button>
                      <Button variant="ghost" size="sm" onClick={handleClearCorpus} disabled={!corpusFiles.length}>
                        Clear corpus
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {corpusFiles.length === 0 ? (
                      <div className="rounded-xl border bg-muted/20 p-6 text-sm text-muted-foreground">
                        No corpus files uploaded yet. Start by adding your training documents.
                      </div>
                    ) : (
                      corpusFiles.map((file) => (
                        <div key={file.path} className="flex items-start justify-between gap-4 rounded-xl border p-4">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{file.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatBytes(file.size)} · Updated {new Date(file.modified).toLocaleString()}
                            </p>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => void handleDeleteCorpusFile(file.path)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="min-w-0">

              <TrainingPanel
                userId={authUser?.id ?? null}
                corpusReady={trainingReady}
                defaultName={profile?.name || authUser?.email?.split('@')[0] || 'sovereign'}
                isAdmin={user?.role === 'admin'}
              />
            </div>
          </div>

          <TrainedModelsSection userId={authUser?.id ?? null} />

          <section className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Additional resources</h2>

              <p className="text-sm text-muted-foreground">
                Secondary tools and reference information live here so the training workflow stays easy to follow.
              </p>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <Card>

                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-canada-red" />
                    Demo Sovereign Models
                  </CardTitle>
                  <CardDescription>
                    Only CanXP demo sovereign models are surfaced here while your own trained model is pending.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-xl border p-4 flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium">MaplePT</p>
                      <p className="text-sm text-muted-foreground mt-1">Canadian-hosted demo access for evaluating sovereign model behavior.</p>
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => handleStartDemoChat(maplePtModel?.id)}
                      disabled={!maplePtModel || isCreatingChat}
                    >
                      Open chat
                    </Button>
                  </div>
                  <div className="rounded-xl border p-4 flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium">MaplePT-Free</p>
                      <p className="text-sm text-muted-foreground mt-1">Limited free sovereign model access for lightweight testing.</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => handleStartDemoChat(mapleFreeModel?.id)}
                      disabled={!mapleFreeModel || isCreatingChat}
                    >
                      Try free demo
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BrainCircuit className="h-5 w-5 text-canada-red" />
                    Sovereign Principles
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex gap-3 rounded-xl border p-4">
                    <Sparkles className="h-4 w-4 mt-0.5 text-canada-red" />
                    <p>Your training corpus stays in your private browser workspace until you are ready to proceed.</p>
                  </div>
                  <div className="flex gap-3 rounded-xl border p-4">
                    <Sparkles className="h-4 w-4 mt-0.5 text-canada-red" />
                    <p>Demo chat access is intentionally limited to CanXP sovereign MaplePT experiences.</p>
                  </div>
                  <div className="flex gap-3 rounded-xl border p-4">
                    <Sparkles className="h-4 w-4 mt-0.5 text-canada-red" />
                    <p>When your custom model is ready, this dashboard becomes the handoff point for testing and operational use.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>
        </div>

      </div>

    </MainLayout>
  );
};

export default Index;
